import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import CompanyAdminSidebar from '@/components/CompanyAdminSidebar';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { FileSpreadsheet, CalendarIcon, Download, Users, Building2 } from 'lucide-react';
import { format, subDays, startOfMonth, endOfMonth } from 'date-fns';
import { cn } from '@/lib/utils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PAYROLL_SYSTEMS = [
  { id: 'xero', name: 'Xero NZ Payroll', description: 'Xero NZ format with dd/mm/yyyy dates and HH:mm times' },
  { id: 'paysauce', name: 'PaySauce', description: 'PaySauce format with badge numbers and paycodes' },
  { id: 'myob', name: 'MYOB NZ', description: 'MYOB NZ format with split names and Card IDs' },
  { id: 'ipayroll', name: 'iPayroll', description: 'iPayroll spreadsheet format with element codes' },
  { id: 'smartly', name: 'Smartly', description: 'Smartly #50/#60/#70 line format (no headers)' },
];

const Reports = () => {
  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState('all');
  const [selectedPayroll, setSelectedPayroll] = useState('');
  const [dateRange, setDateRange] = useState({
    from: startOfMonth(new Date()),
    to: endOfMonth(new Date())
  });
  const [loading, setLoading] = useState(false);
  const [payrollLoading, setPayrollLoading] = useState(false);
  const [loadingEmployees, setLoadingEmployees] = useState(true);

  const fetchEmployees = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/employees`);
      setEmployees(response.data);
    } catch (error) {
      console.error('Failed to fetch employees:', error);
    } finally {
      setLoadingEmployees(false);
    }
  }, []);

  useEffect(() => {
    fetchEmployees();
  }, [fetchEmployees]);

  const handleDownload = async () => {
    if (!dateRange.from || !dateRange.to) {
      toast.error('Please select a date range');
      return;
    }

    setLoading(true);
    try {
      const params = {
        start_date: dateRange.from.toISOString(),
        end_date: dateRange.to.toISOString(),
      };
      if (selectedEmployee !== 'all') {
        params.user_id = selectedEmployee;
      }

      const response = await axios.post(`${API}/reports/generate`, params, {
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `time_report_${format(dateRange.from, 'yyyyMMdd')}_${format(dateRange.to, 'yyyyMMdd')}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success('Report downloaded successfully!');
    } catch (error) {
      toast.error('Failed to generate report');
      console.error('Report error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePayrollExport = async () => {
    if (!dateRange.from || !dateRange.to) {
      toast.error('Please select a date range');
      return;
    }
    if (!selectedPayroll) {
      toast.error('Please select a payroll system');
      return;
    }

    setPayrollLoading(true);
    try {
      const response = await axios.post(`${API}/reports/payroll-export`, {
        start_date: format(dateRange.from, 'yyyy-MM-dd'),
        end_date: format(dateRange.to, 'yyyy-MM-dd'),
        payroll_system: selectedPayroll
      }, {
        responseType: 'blob'
      });

      const systemName = PAYROLL_SYSTEMS.find(s => s.id === selectedPayroll)?.name || selectedPayroll;
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `payroll_${selectedPayroll}_${format(dateRange.from, 'yyyyMMdd')}_${format(dateRange.to, 'yyyyMMdd')}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success(`${systemName} payroll export downloaded!`);
    } catch (error) {
      toast.error('Failed to generate payroll export');
      console.error('Payroll export error:', error);
    } finally {
      setPayrollLoading(false);
    }
  };

  const quickRanges = [
    { label: 'Today', onClick: () => setDateRange({ from: new Date(), to: new Date() }) },
    { label: 'Last 7 days', onClick: () => setDateRange({ from: subDays(new Date(), 7), to: new Date() }) },
    { label: 'Last 30 days', onClick: () => setDateRange({ from: subDays(new Date(), 30), to: new Date() }) },
    { label: 'This month', onClick: () => setDateRange({ from: startOfMonth(new Date()), to: endOfMonth(new Date()) }) },
  ];

  return (
    <div className="flex flex-col md:flex-row min-h-screen bg-slate-50 w-full overflow-x-hidden">
      <CompanyAdminSidebar />
      
      <main className="flex-1 min-w-0 p-3 sm:p-4 md:p-8 pb-20 md:pb-8 overflow-x-hidden w-full">
        <div className="max-w-4xl mx-auto space-y-4 sm:space-y-6">
          {/* Header */}
          <div>
            <h1 className="text-xl sm:text-2xl md:text-3xl font-bold font-['Manrope'] tracking-tight">Reports</h1>
            <p className="text-sm sm:text-base text-slate-500">Generate and download attendance reports</p>
          </div>

          {/* Report Builder */}
          <Card className="border-0 shadow-lg">
            <CardHeader>
              <CardTitle className="font-['Manrope'] flex items-center gap-2">
                <FileSpreadsheet className="w-5 h-5" />
                Generate Report
              </CardTitle>
              <CardDescription>
                Select date range and employee to generate a CSV report
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Quick Date Ranges */}
              <div className="space-y-2">
                <Label>Quick Select</Label>
                <div className="flex flex-wrap gap-2">
                  {quickRanges.map((range) => (
                    <Button
                      key={range.label}
                      variant="outline"
                      size="sm"
                      onClick={range.onClick}
                      data-testid={`quick-range-${range.label.toLowerCase().replace(/\s/g, '-')}`}
                    >
                      {range.label}
                    </Button>
                  ))}
                </div>
              </div>

              {/* Date Range Picker */}
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Start Date</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "w-full justify-start text-left font-normal",
                          !dateRange.from && "text-muted-foreground"
                        )}
                        data-testid="start-date-picker"
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {dateRange.from ? format(dateRange.from, "dd/MM/yyyy") : "Pick a date"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={dateRange.from}
                        onSelect={(date) => setDateRange(prev => ({ ...prev, from: date }))}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                <div className="space-y-2">
                  <Label>End Date</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "w-full justify-start text-left font-normal",
                          !dateRange.to && "text-muted-foreground"
                        )}
                        data-testid="end-date-picker"
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {dateRange.to ? format(dateRange.to, "dd/MM/yyyy") : "Pick a date"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={dateRange.to}
                        onSelect={(date) => setDateRange(prev => ({ ...prev, to: date }))}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              </div>

              {/* Employee Select */}
              <div className="space-y-2">
                <Label>Employee</Label>
                <Select value={selectedEmployee} onValueChange={setSelectedEmployee}>
                  <SelectTrigger data-testid="employee-select">
                    <SelectValue placeholder="Select employee" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">
                      <div className="flex items-center gap-2">
                        <Users className="w-4 h-4" />
                        All Employees
                      </div>
                    </SelectItem>
                    {loadingEmployees ? (
                      <SelectItem value="loading" disabled>Loading...</SelectItem>
                    ) : (
                      employees.map((emp) => (
                        <SelectItem key={emp.id} value={emp.id}>
                          {emp.name}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              </div>

              {/* Download Button */}
              <Button
                onClick={handleDownload}
                disabled={loading || !dateRange.from || !dateRange.to}
                className="w-full h-12 gap-2 bg-[hsl(243,65%,20%)] hover:bg-[hsl(243,65%,25%)]"
                data-testid="download-report-btn"
              >
                {loading ? (
                  <div className="spinner" />
                ) : (
                  <>
                    <Download className="w-5 h-5" />
                    Download CSV Report
                  </>
                )}
              </Button>
            </CardContent>
          </Card>

          {/* Report Info */}
          <Card className="border-0 shadow-lg bg-slate-50">
            <CardContent className="pt-6">
              <div className="space-y-4">
                <h3 className="font-medium font-['Manrope']">Report Contents</h3>
                <p className="text-sm text-slate-600">
                  The generated CSV report will include:
                </p>
                <ul className="text-sm text-slate-500 space-y-2">
                  <li className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-[hsl(243,65%,20%)]" />
                    Employee name
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-[hsl(243,65%,20%)]" />
                    Date of each work day
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-[hsl(243,65%,20%)]" />
                    Office location
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-[hsl(243,65%,20%)]" />
                    Gross hours (total time clocked in)
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-[hsl(var(--warning))]" />
                    Break time (breakfast + lunch breaks)
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-[hsl(var(--success))]" />
                    Net hours worked (gross hours minus breaks)
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-[hsl(243,65%,20%)]" />
                    Total net hours for the selected period
                  </li>
                </ul>
              </div>
            </CardContent>
          </Card>

          {/* Payroll Export */}
          <Card className="border-0 shadow-lg">
            <CardHeader>
              <CardTitle className="font-['Manrope'] flex items-center gap-2">
                <Building2 className="w-5 h-5" />
                NZ Payroll Export
              </CardTitle>
              <CardDescription>
                Export timesheet and leave data for NZ payroll systems (Xero, PaySauce, MYOB, iPayroll, Smartly)
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Payroll System Select */}
              <div className="space-y-2">
                <Label>Payroll System</Label>
                <Select value={selectedPayroll} onValueChange={setSelectedPayroll}>
                  <SelectTrigger data-testid="payroll-system-select">
                    <SelectValue placeholder="Select your NZ payroll system" />
                  </SelectTrigger>
                  <SelectContent>
                    {PAYROLL_SYSTEMS.map((system) => (
                      <SelectItem key={system.id} value={system.id}>
                        <div className="flex flex-col">
                          <span className="font-medium">{system.name}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {selectedPayroll && (
                  <p className="text-xs text-slate-500">
                    {PAYROLL_SYSTEMS.find(s => s.id === selectedPayroll)?.description}
                  </p>
                )}
              </div>

              {/* Date Range Info */}
              <div className="p-4 bg-slate-50 rounded-lg">
                <p className="text-sm text-slate-600">
                  <strong>Selected Period:</strong> {dateRange.from && dateRange.to 
                    ? `${format(dateRange.from, 'dd/MM/yyyy')} - ${format(dateRange.to, 'dd/MM/yyyy')}`
                    : 'Please select a date range above'}
                </p>
              </div>

              {/* Export Button */}
              <Button
                onClick={handlePayrollExport}
                disabled={payrollLoading || !dateRange.from || !dateRange.to || !selectedPayroll}
                className="w-full h-12 gap-2 bg-emerald-600 hover:bg-emerald-700"
                data-testid="payroll-export-btn"
              >
                {payrollLoading ? (
                  <div className="spinner" />
                ) : (
                  <>
                    <Download className="w-5 h-5" />
                    Export for {PAYROLL_SYSTEMS.find(s => s.id === selectedPayroll)?.name || 'Payroll'}
                  </>
                )}
              </Button>

              {/* Payroll Export Info */}
              <div className="text-xs text-slate-500 space-y-1">
                <p><strong>Exported data includes:</strong></p>
                <ul className="list-disc list-inside space-y-0.5 ml-2">
                  <li>Employee worked hours (from clock-in/out records)</li>
                  <li>Employee Payroll ID / Badge (set in Employee Management)</li>
                  <li>Annual leave (from roster entries)</li>
                  <li>Sick leave (from roster entries)</li>
                  <li>Public holidays taken/worked (from roster entries)</li>
                  <li>Alternative holidays (from roster entries)</li>
                  <li>Bereavement leave (from roster entries)</li>
                </ul>
                <p className="mt-2 text-slate-400">
                  <strong>Tip:</strong> Make sure employees have their Payroll ID set in Employee Management for accurate matching.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
};

export default Reports;
